import {
    enqueueSnackbar as notistackEnqueueSnackbar,
    type OptionsObject,
    type SnackbarKey,
    type SnackbarMessage,
} from 'notistack';
import SnackbarCloseButton from '~/components/SnackbarCloseButton';

declare module 'notistack' {
    interface OptionsObject {
        /**
         * PHI-free variant of the snackbar text used for the developer-console
         * mirror in `WarningSnackbarContent`. The user-facing `message` may
         * embed patient-identifiable values (e.g. skipped-file names); call
         * sites that include such data must pass a redacted `logMessage` so
         * PHI never reaches browser logs or log collectors. When omitted, the
         * `message` itself is logged, which is safe only for messages that
         * carry no PHI. The `logMessage` must itself be PHI-free.
         */
        logMessage?: string;
    }
}

/**
 * How long (ms) warning and error snackbars stay on screen before auto-hiding.
 * They carry the most important information, so they linger longer than
 * notistack's 5s default to give users time to read them.
 */
export const NOTIFICATION_AUTO_HIDE_MS = 16000;

/**
 * App-wide replacement for notistack's `enqueueSnackbar` that gives `warning`
 * and `error` snackbars a longer, consistent on-screen duration and a close
 * button so the user can dismiss them manually.
 *
 * notistack v3 has no per-variant defaults, so centralizing it here keeps
 * every warning/error aligned without each call site repeating the config. A
 * caller can still override the duration or action by passing its own.
 */
export function enqueueSnackbar(
    message: SnackbarMessage,
    options?: OptionsObject
): SnackbarKey {
    const variant = options?.variant;
    const isLongLived = variant === 'warning' || variant === 'error';
    return notistackEnqueueSnackbar(message, {
        ...(isLongLived
            ? {
                  autoHideDuration: NOTIFICATION_AUTO_HIDE_MS,
                  action: (key: SnackbarKey) => (
                      <SnackbarCloseButton snackbarKey={key} />
                  ),
              }
            : {}),
        ...options,
    });
}
